import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import accountContext from "../../account/accountContext";
import { Roles } from "../../account/models/AccountModels";

/**
 * Route guard that confines the MidasPilot role to the MIDAS surface
 * (MIDAS_PILOT_ROLE_SPEC.md §5.2). A pilot is an EXTERNAL pilot-customer user that
 * must see nothing but /midas — every other in-app path funnels back there.
 *
 * This is the UX half of the confinement only. The load-bearing controls are
 * server-side (fail-closed PilotAccess tenant allowlist + the seeded module
 * permissions); never treat this guard as a security boundary.
 *
 * Exemption is an explicit ALLOWLIST of the two paths the spec promises a pilot
 * (`/midas` + `/midas/history`) — deliberately not a `startsWith` prefix:
 *   - `startsWith("/midas")` would exempt the legacy `/midas-v1` / `/midas-v2` routes,
 *     which acceptance criterion §10.2 lists among the URLs a pilot must be redirected
 *     away from.
 *   - `startsWith("/midas/")` would still exempt `/midas/_fixtures`, the unlinked dev
 *     fixture harness — i.e. exactly the dev-tool-tree accumulation that §5.2's mount
 *     ruling cites as the reason to contain structurally rather than by carve-out.
 * Verified 2026-08-19: every in-app navigation target under `/midas` is one of these
 * two (`navigate("/midas")` / `navigate("/midas/history")`); nothing links `_fixtures`.
 * If a genuine new pilot-facing sub-page is added, add it HERE explicitly.
 *
 * The pathname is lowercased before the check: React Router matches routes
 * case-insensitively but `location.pathname` preserves the URL's original case, so a
 * case-variant URL (e.g. `/Midas/History`) renders the MIDAS route while reading as
 * non-MIDAS here — same class of bug as the documented `SettingsV2AliasRedirect`
 * `i`-flag trap in useCustomerRoutes.tsx.
 *
 * Mounted ONCE, in App.tsx, as a layout-route wrapper around the `/` Layout route's
 * children (§5.2 mount point AMENDED 2026-08-18) — NOT inside useCustomerRoutes. That
 * placement is what covers the `tech` sibling tree, which carries no AuthorizedRoute of
 * its own, plus any Layout child added later. Do NOT also mount it inside a child route
 * tree: the guard would then run twice on the same navigation.
 *
 * Usage:
 * ```tsx
 * <Route path="/" element={<Layout />}>
 *   <Route element={<PilotRouteGuard />}>
 *     ... admin / tech / customer trees ...
 *   </Route>
 * </Route>
 * ```
 */
/** The ONLY paths a MidasPilot may reach. Lowercase — `pathname` is lowercased before
 *  comparison because React Router matches case-insensitively while preserving case. */
const PILOT_ALLOWED_PATHS = ["/midas", "/midas/history"];

const PilotRouteGuard = () => {
  const user = accountContext.user;
  const { pathname } = useLocation();

  // If not logged in, let AuthorizedRoute handle the redirect
  if (!user) {
    return <Outlet />;
  }

  // Every other role is completely unaffected — no behaviour change.
  if (user.role !== Roles.MIDAS_PILOT) {
    return <Outlet />;
  }

  const path = pathname.toLowerCase();
  const isMidasRoute = PILOT_ALLOWED_PATHS.includes(path);

  if (!isMidasRoute) {
    return <Navigate to="/midas" replace />;
  }

  return <Outlet />;
};

export default PilotRouteGuard;
